import React, { useState, useEffect, useRef } from 'react';
import framework from 'utils/framework';
const { wrapFunctionalComponent } = framework;
import LanguageLocaleUtils from 'utils/LanguageLocale';
import userUtils from 'utils/User';
import {
    Box, Flex, Text, Button, Link, Grid, Divider
} from 'components/ui';
import InputEmail from 'components/Inputs/InputEmail/InputEmail';
import PasswordRevealInput from 'components/Inputs/PasswordRevealInput';
import ErrorList from 'components/ErrorList';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import externalAuthService from 'services/api/authentication/externalAuth';
import UserActions from 'actions/UserActions';
import store from 'Store';
import Actions from 'Actions';
import Chevron from 'components/Chevron/Chevron';
import Loader from 'components/Loader';
import UrlUtils from 'utils/Url';
import {
    borders, colors, space, fontSizes, fontWeights, lineHeights, radii
} from 'style/config';
import formValidator from 'utils/FormValidator';
import ErrorConstantsUtils from 'utils/ErrorConstants';
import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import linkTrackingError from 'analytics/bindings/pages/all/linkTrackingError';
import {
    handleGrant, checkIsOAuth2Flow, extractOAuth2Params, storeOAuth2Params
} from 'utils/authGrant';
import LegalCompliance from 'components/Login/LegalCompliance/LegalCompliance.f';
import LoginEN from 'pages/Auth/locales/Login_en_US';
import LoginFR from 'pages/Auth/locales/Login_fr_CA';

const getText = LanguageLocaleUtils.getLocalisedDataFromResource(LoginEN, LoginFR);
const { ERROR_CODES } = ErrorConstantsUtils;
const LOADER_WIDTH = 400;

/**
 * Login Page
 * Full-screen login page that handles both regular login and OAuth2 external authentication
 * Supports two flows:
 * OAuth2 Flow (when client_id and redirect_uri params present):
 * 1. User is redirected here from backend /oauth2/authorize endpoint (302 redirect)
 * 2. URL contains OAuth2 protocol parameters for external authentication
 * 3. User enters email and password
 * 4. Submit credentials to backend /oauth2/login API endpoint
 * 5. On success: receive session_id, token, hasIdentity
 * 6. Trigger standard Sephora login flow using UserActions.signIn
 * Regular Login Flow (when OAuth2 params not present):
 * 1. User enters email and password
 * 2. Call UserActions.signIn directly with regular login callback
 * 3. Redirect to home page or intended destination after successful login
 */
const Login = () => {
    const emailInput = useRef(null);
    const passwordInput = useRef(null);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errorMessages, setErrorMessages] = useState([]);
    const [isAutoLoginInProgress, setIsAutoLoginInProgress] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [externalAuthParams, setExternalAuthParams] = useState(null);

    // Determine if this is an OAuth2 flow based on presence of required OAuth2 parameters
    const isOAuth2Flow = externalAuthParams ? checkIsOAuth2Flow(externalAuthParams) : false;

    // Check if logout is in progress on initial load
    useEffect(() => {
        // Check if RefreshToken service has flagged for signout on init
        if (window.Sephora?.Util?.RefreshToken?.isSignOutOnInit) {
            setIsLoggingOut(true);
            store.dispatch(Actions.showInterstice(true));
        }
    }, []);

    // Populate email and external auth params from URL on client-side
    // eslint-disable-next-line complexity
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);

        const params = extractOAuth2Params();
        const existingUser = urlParams.get('existingUser');

        if (existingUser) {
            setErrorMessages([getText('existingUserError')]);
            setEmail(existingUser);
        }

        setExternalAuthParams(params);

        // Pre-populate email if login_hint is provided
        if (params.loginHint) {
            setEmail(params.loginHint);
        }

        // Store external auth params in localStorage for use after login (OAuth2 flow only)
        storeOAuth2Params(params);

        // Handle logged-in users
        const handleLoggedInUser = async () => {
            await userUtils.validateUserStatus();

            const isSignedIn = userUtils.isSignedIn();

            if (isSignedIn) {
                if (params.clientId && params.redirectUri) {
                    // OAuth2 flow: Auto-login with stored credentials
                    await attemptAutoLogin(params);
                } else {
                    // Non-OAuth2 flow: Redirect to home page
                    UrlUtils.redirectTo('/');
                }
            }
        };

        handleLoggedInUser();
    }, []);

    /**
     * Attempt automatic login for already logged-in users with OAuth2 params
     * Uses stored email and access token instead of password
     */
    const attemptAutoLogin = async params => {
        try {
            setIsAutoLoginInProgress(true);
            store.dispatch(Actions.showInterstice(true));

            const accessToken = Storage.local.getItem(LOCAL_STORAGE.AUTH_ACCESS_TOKEN) || undefined;

            const sessionId = await performExternalAuth({
                accessToken,
                ...params
            });

            // Navigate to authorize page
            store.dispatch(Actions.showInterstice(false));
            handleGrant(sessionId, setErrorMessages, store.dispatch);
        } catch (error) {
            // Auto-login failed - show modal and allow manual login
            store.dispatch(Actions.showInterstice(false));
            setIsAutoLoginInProgress(false);

            const redirectUrl = window.location.pathname + window.location.search;

            // Show verification failed modal
            store.dispatch(
                Actions.showInfoModal({
                    isOpen: true,
                    title: getText('verificationFailedTitle'),
                    message: getText('verificationFailedMessage'),
                    buttonText: getText('verificationFailedButton'),
                    callback: () => {
                        // Close modal and show loading state
                        store.dispatch(Actions.showInfoModal({ isOpen: false }));
                        setIsLoggingOut(true);
                        store.dispatch(Actions.showInterstice(true));
                        // Log user out and stay on current page to show login form
                        store.dispatch(UserActions.signOut(redirectUrl, false, false));
                    },
                    cancelCallback: () => {
                        // Close modal and show loading state
                        store.dispatch(Actions.showInfoModal({ isOpen: false }));
                        setIsLoggingOut(true);
                        store.dispatch(Actions.showInterstice(true));
                        // Log user out and stay on current page to show login form
                        store.dispatch(UserActions.signOut(redirectUrl, false, false));
                    },
                    showCloseButton: false
                })
            );
        }
    };

    const handleEmailChange = e => {
        setEmail(e.target.value);
    };

    const handlePasswordChange = e => {
        setPassword(e.target.value);
    };

    const validatePassword = pass => {
        if (formValidator.isEmpty(pass)) {
            return getText('enterPasswordErrorMessage');
        }

        if (!formValidator.isValidLength(pass, 6, 12)) {
            return getText('passwordLengthInvalid');
        }

        return null;
    };

    const isValid = () => {
        const fieldsForValidation = [emailInput.current, passwordInput.current];
        const errors = formValidator.getErrors(fieldsForValidation);
        const topLevelErrors = [];

        // For OAuth2 flow, validate required external auth parameters
        if (isOAuth2Flow) {
            if (!externalAuthParams.clientId) {
                topLevelErrors.push('Missing required parameter: client_id');
            }

            if (!externalAuthParams.redirectUri) {
                topLevelErrors.push('Missing required parameter: redirect_uri');
            }
        }

        if (topLevelErrors.length) {
            setErrorMessages(topLevelErrors);
        }

        return !errors.fields.length && !topLevelErrors.length;
    };

    const handleSubmit = async e => {
        e.preventDefault();
        setErrorMessages([]);

        if (!isValid()) {
            return;
        }

        store.dispatch(Actions.showInterstice(true));

        try {
            if (isOAuth2Flow) {
                // OAuth2 External Auth Flow - Call external auth login endpoint
                await handleExternalAuthLogin();
            } else {
                // Regular Login Flow - Call standard login directly
                await handleRegularLogin();
            }
        } catch (error) {
            const errorMessage = error?.errorMessage || error?.message || 'Login failed. Please try again.';

            setErrorMessages([errorMessage]);
            store.dispatch(Actions.showInterstice(false));
        }
    };

    /**
     * Handle regular login flow
     * Standard Sephora login without OAuth2 external authentication
     */
    const handleRegularLogin = async () => {
        const successCallback = () => {
            // After successful login, redirect to home page or intended destination
            store.dispatch(Actions.showInterstice(false));
            UrlUtils.redirectTo('/');
        };

        const errorCallback = json => {
            // Handle errors the same way as SignInModal
            if (json?.errorMessages) {
                if (json?.errorCode === ERROR_CODES.TS_HEADER_ERROR) {
                    setErrorMessages([getText('unableToLogYouIn')]);
                } else if (json.errorMessages[0]) {
                    setErrorMessages(json.errorMessages);
                } else if (json.responseStatus >= 400) {
                    setErrorMessages([getText('genericErrorMessage')]);
                }

                throw { errorMessage: json.errorMessages[0] };
            }
        };

        await store.dispatch(
            UserActions.signIn(email, password, true, false, successCallback, errorCallback, false, null, false, false, false, false)
        );
    };

    const signInFailureTrack = (fieldErrors, errors = null) => {
        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                bindingMethods: linkTrackingError,
                eventStrings: [anaConsts.Event.SIGN_IN_ATTEMPT, anaConsts.Event.SIGN_IN_FAILED, anaConsts.Event.EVENT_313],
                prop43: 'manual',
                actionInfo: anaConsts.LinkData.CHATGPT_FAILED_SIGN_IN,
                pageName: anaConsts.CHATGPT_SIGN_IN_PAGE_NAME,
                pageType: anaConsts.PAGE_TYPES.SIGN_IN,
                pageDetail: anaConsts.PAGE_DETAIL.SIGN_IN,
                fieldErrors: `${anaConsts.CHATGPT}:${fieldErrors?.join(',') || 'n/a'}`,
                errorMessages: errors || fieldErrors
            }
        });
    };

    const performExternalAuth = async (params, errorType = 'checkEmailPassword') => {
        const externalAuthResponse = await externalAuthService.externalAuthLogin({
            ...params
        });
        const sessionId = externalAuthResponse?.sessionId;

        if (!sessionId) {
            throw { errorMessage: getText(errorType), errorDescription: externalAuthResponse?.error_description };
        }

        return sessionId;
    };

    const handleExternalAuthError = error => {
        const errorMessage = error?.errorMessage || getText('checkEmailPassword');
        const trackError = error?.error_description || error?.errorDescription || error?.message || errorMessage;
        setErrorMessages([errorMessage]);

        const fieldErrors = [];

        if (error?.error_description?.includes('email') || error?.errorMessage?.includes('email')) {
            fieldErrors.push('email');
        }

        if (error?.error_description?.includes('password') || error?.errorMessage?.includes('password')) {
            fieldErrors.push('password');
        }

        signInFailureTrack(fieldErrors, [trackError]);
    };

    /**
     * Handle external auth login flow
     * Authenticates user and initiates OAuth2.0 authorization flow for third-party integrations
     * Calls backend /oauth2/login endpoint with credentials and OAuth2 protocol parameters
     */
    const handleExternalAuthLogin = async () => {
        const isExternalAuthFirst = Sephora.configurationSettings?.isExternalAuthFirst;
        try {
            let sessionId;

            if (!isExternalAuthFirst) {
                sessionId = await performExternalAuth({
                    email,
                    password,
                    ...externalAuthParams
                });
            }

            const completeExternalAuthAfterLogin = async () => {
                try {
                    await new Promise(resolve => setTimeout(resolve, 0));
                    store.dispatch(Actions.showInterstice(true));

                    const accessToken = Storage.local.getItem(LOCAL_STORAGE.AUTH_ACCESS_TOKEN) || undefined;
                    sessionId = await performExternalAuth({
                        accessToken,
                        ...externalAuthParams
                    });

                    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                        data: {
                            eventStrings: [anaConsts.Event.SIGN_IN_ATTEMPT, anaConsts.Event.SIGN_IN_SUCCESS, anaConsts.Event.EVENT_313],
                            prop43: 'manual',
                            actionInfo: anaConsts.LinkData.CHATGPT_SUCCESSFUL_SIGN_IN,
                            pageName: anaConsts.CHATGPT_SIGN_IN_PAGE_NAME,
                            pageType: anaConsts.PAGE_TYPES.SIGN_IN,
                            pageDetail: anaConsts.PAGE_DETAIL.SIGN_IN
                        }
                    });

                    store.dispatch(Actions.showInterstice(false));
                    handleGrant(sessionId, setErrorMessages, store.dispatch);
                } catch (error) {
                    handleExternalAuthError(error);
                    store.dispatch(Actions.showInterstice(false));
                }
            };

            // This ensures the user is fully authenticated before proceeding to authorization
            const successCallback = () => {
                if (isExternalAuthFirst) {
                    completeExternalAuthAfterLogin();
                } else {
                    handleGrant(sessionId, setErrorMessages, store.dispatch);
                }
            };

            if (userUtils.isSignedIn()) {
                successCallback();

                return;
            }

            await store.dispatch(
                UserActions.signIn(
                    email,
                    password,
                    true,
                    false,
                    successCallback,
                    json => {
                        // Handle errors the same way as SignInModal
                        if (json?.errorMessages) {
                            if (json?.errorCode === ERROR_CODES.TS_HEADER_ERROR) {
                                setErrorMessages([getText('unableToLogYouIn')]);
                            } else if (json.errorMessages[0]) {
                                setErrorMessages(json.errorMessages);
                            } else if (json.responseStatus >= 400) {
                                setErrorMessages([getText('genericErrorMessage')]);
                            }

                            // AC2: Fire s.tl error tracking for chatgpt sign-in failure
                            signInFailureTrack(json.errorMessages);
                        }
                    },
                    false,
                    null,
                    false,
                    false,
                    false,
                    { isIntegrationLoginAttempt: isOAuth2Flow } // Pass extra param to indicate this is an integration login attempt (OAuth2 flow)
                )
            );
        } catch (error) {
            handleExternalAuthError(error);
        } finally {
            !isExternalAuthFirst && store.dispatch(Actions.showInterstice(false));
        }
    };

    const handleCreateAccount = () => {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.delete('existingUser');
        const params = urlParams.toString();
        UrlUtils.redirectTo(`/register${params ? `?${params}` : ''}`);
    };

    const handleContinueAsGuest = async () => {
        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                eventStrings: [anaConsts.Event.EVENT_313],
                actionInfo: anaConsts.LinkData.CHATGPT_CONTINUE_AS_GUEST,
                pageName: anaConsts.CHATGPT_SIGN_IN_PAGE_NAME,
                pageType: anaConsts.PAGE_TYPES.SIGN_IN,
                pageDetail: anaConsts.PAGE_DETAIL.SIGN_IN
            }
        });

        try {
            if (isOAuth2Flow) {
                const sessionId = await performExternalAuth(
                    {
                        email: null,
                        password: null,
                        ...externalAuthParams
                    },
                    'errorMessage'
                );

                handleGrant(sessionId, setErrorMessages, store.dispatch);
            } else {
                // For regular login, redirect to home page
                UrlUtils.redirectTo('/');
            }
        } catch (error) {
            const errorMessage = error?.errorMessage || error?.message || getText('errorMessage');
            setErrorMessages([errorMessage]);
            store.dispatch(Actions.showInterstice(false));
        }
    };

    const handleClose = () => {
        window.history.back();
    };

    return (
        <div>
            <Box
                minHeight='100vh'
                display='flex'
                flexDirection='column'
                alignItems='center'
                justifyContent='flex-start'
            >
                <Flex css={styles.headerContainer}>
                    {/* Back Button */}
                    <Box
                        is='button'
                        type='button'
                        aria-label={getText('backButtonAriaLabel')}
                        css={styles.backButton}
                        onClick={handleClose}
                    >
                        <Chevron
                            direction={'left'}
                            color={colors.black}
                            css={styles.chevron}
                        />
                    </Box>

                    {/* Logo */}
                    <Box css={styles.logoContainer}>
                        <img
                            src='/img/ufe/logo.svg'
                            alt='Sephora'
                            style={styles.logo}
                        />
                    </Box>
                </Flex>
                <Box css={styles.contentWrapper}>
                    {/* Don't show form while auto-login or logout is in progress */}
                    {!externalAuthParams || isAutoLoginInProgress || isLoggingOut ? (
                        !externalAuthParams ? (
                            <Loader
                                isShown={true}
                                isInline={true}
                                minHeight={400}
                                style={{
                                    width: LOADER_WIDTH
                                }}
                            />
                        ) : null
                    ) : (
                        <>
                            {/* Page Title */}
                            <Box
                                maxWidth='400px'
                                width='100%'
                                css={styles.titleContainer}
                            >
                                <Text
                                    is='h1'
                                    fontSize={fontSizes.lg}
                                    fontWeight={fontWeights.bold}
                                    lineHeight={lineHeights.tight}
                                >
                                    {getText('pageTitle')}
                                </Text>
                            </Box>

                            {/* Benefits Section */}
                            <Box
                                maxWidth='400px'
                                width='100%'
                                css={styles.benefitsContainer}
                            >
                                <Grid
                                    gridTemplateColumns='repeat(3, 1fr)'
                                    css={styles.benefitsGrid}
                                >
                                    <Box
                                        display='flex'
                                        flexDirection='column'
                                        alignItems='center'
                                        textAlign='center'
                                    >
                                        <Box>
                                            <img
                                                src='/img/ufe/auth/makeup.svg'
                                                alt='makeup'
                                                style={styles.benefitIcon}
                                            />
                                        </Box>
                                        <Text
                                            fontSize={fontSizes.sm}
                                            lineHeight='14px'
                                            color={colors.black}
                                        >
                                            {getText('personalizedBeautyTitle')}
                                        </Text>
                                    </Box>
                                    <Box
                                        display='flex'
                                        flexDirection='column'
                                        alignItems='center'
                                        textAlign='center'
                                    >
                                        <Box>
                                            <img
                                                src='/img/ufe/auth/earn-points.svg'
                                                alt='earn points'
                                                style={styles.benefitIcon}
                                            />
                                        </Box>
                                        <Text
                                            fontSize={fontSizes.sm}
                                            lineHeight='14px'
                                            color={colors.black}
                                        >
                                            {getText('perksTitle')}
                                        </Text>
                                    </Box>
                                    <Box
                                        display='flex'
                                        flexDirection='column'
                                        alignItems='center'
                                        textAlign='center'
                                    >
                                        <Box>
                                            <img
                                                src='/img/ufe/auth/chat.svg'
                                                alt='chat'
                                                style={styles.benefitIcon}
                                            />
                                        </Box>
                                        <Text
                                            fontSize={fontSizes.sm}
                                            lineHeight='14px'
                                            color={colors.black}
                                        >
                                            {getText('adviceTitle')}
                                        </Text>
                                    </Box>
                                </Grid>
                            </Box>

                            {/* Form Container with 400px max-width */}
                            <Box
                                maxWidth='400px'
                                width='100%'
                            >
                                {/* Login Form */}
                                <form
                                    onSubmit={handleSubmit}
                                    noValidate={true}
                                >
                                    {/* Error Messages */}
                                    {errorMessages.length > 0 && (
                                        <Box css={styles.errorContainer}>
                                            <ErrorList errorMessages={errorMessages} />
                                        </Box>
                                    )}
                                    <Box css={styles.emailContainer}>
                                        <InputEmail
                                            id='login-email-input'
                                            name='email'
                                            ref={emailInput}
                                            login={email}
                                            onChange={handleEmailChange}
                                            label={getText('emailPlaceholder')}
                                            autoFocus={!email}
                                            hideAsterisk={true}
                                        />
                                    </Box>

                                    <Box css={styles.passwordContainer}>
                                        <PasswordRevealInput
                                            id='login-password-input'
                                            marginBottom={0}
                                            name='password'
                                            ref={passwordInput}
                                            value={password}
                                            onChange={handlePasswordChange}
                                            label={getText('passwordPlaceholder')}
                                            hideAsterisk={true}
                                            validate={validatePassword}
                                        />
                                    </Box>

                                    {/* Forgot Password Link */}
                                    <Box
                                        css={styles.forgotPasswordContainer}
                                        textAlign='right'
                                    >
                                        <Link
                                            href='/resetpassword'
                                            color={colors.blue}
                                            fontSize={fontSizes.base}
                                        >
                                            {getText('forgotPassword')}
                                        </Link>
                                    </Box>

                                    <LegalCompliance isOAuth2Flow={isOAuth2Flow} />

                                    {/* Sign In Button */}
                                    <Button
                                        type='submit'
                                        width='100%'
                                        variant='primary'
                                        paddingY={space[3]}
                                    >
                                        {isOAuth2Flow ? getText('signInAllowAccess') : getText('signInButton')}
                                    </Button>

                                    {/* Divider with spacing */}
                                    <Box css={styles.dividerContainer}>
                                        <Divider thick />
                                    </Box>

                                    {Sephora.configurationSettings?.integrationSignupEnabled ? (
                                        <Button
                                            type='button'
                                            width='100%'
                                            variant='secondary'
                                            onClick={handleCreateAccount}
                                            paddingY={space[3]}
                                            marginBottom={space[3]}
                                        >
                                            {getText('createAccount')}
                                        </Button>
                                    ) : null}

                                    {/* Continue as Guest Button */}
                                    {Sephora.configurationSettings?.integrations_guest_allowed && (
                                        <Button
                                            type='button'
                                            width='100%'
                                            variant='secondary'
                                            onClick={handleContinueAsGuest}
                                            paddingY={space[3]}
                                        >
                                            {getText('continueAsGuest')}
                                        </Button>
                                    )}
                                </form>
                            </Box>
                        </>
                    )}
                </Box>
                {/* End Form Container */}
            </Box>
        </div>
    );
};

const styles = {
    headerContainer: {
        position: 'relative',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'center',
        borderBottom: `${borders[1]} ${colors.lightGray}`
    },
    backButton: {
        position: 'absolute',
        top: space[5],
        left: space[5],
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: space[2],
        margin: -space[2],
        borderRadius: radii[2],
        transition: 'background-color 0.2s',
        ':hover': {
            backgroundColor: colors.lightGray
        },
        ':focus:not(:focus-visible)': {
            outline: 'none'
        },
        ':focus-visible': {
            outline: `2px solid ${colors.black}`,
            outlineOffset: 2
        }
    },
    chevron: {
        width: space[4],
        height: space[4]
    },
    logoContainer: {
        marginBottom: space[5],
        marginTop: space[5]
    },
    logo: {
        height: space[5],
        width: 'auto'
    },
    contentWrapper: {
        marginTop: space[7],
        paddingLeft: space[3],
        paddingRight: space[3]
    },
    titleContainer: {
        marginBottom: space[6]
    },
    benefitsContainer: {
        marginTop: space[5],
        marginBottom: space[5]
    },
    benefitsGrid: {
        gap: space[4]
    },
    benefitIcon: {
        height: space[7],
        width: 'auto'
    },
    errorContainer: {
        marginBottom: space[4]
    },
    emailContainer: {
        marginBottom: space[4]
    },
    passwordContainer: {
        marginBottom: space[2]
    },
    forgotPasswordContainer: {
        marginBottom: space[4]
    },
    dividerContainer: {
        marginTop: space[5],
        marginBottom: space[5]
    }
};

export default wrapFunctionalComponent(Login, 'Login');
